import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Box,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { employeeService } from '../../services/employeeService';
import { projectService } from '../../services/projectService';
import { locationService } from '../../services/locationService';
import assignmentService from '../../services/assignmentService';
import { format } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import PageHeader from '../Common/PageHeader';
import ConfirmDialog from '../Common/ConfirmDialog';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Table header styles
const headerCellStyle = {
  backgroundColor: '#5C6BC0',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  borderBottom: '3px solid #3949AB',
  textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
};

const EmployeeList = () => {
  const navigate = useNavigate();
  const [allEmployees, setAllEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [selectedProject, setSelectedProject] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [nameFilter, setNameFilter] = useState('');
  const [empIdFilter, setEmpIdFilter] = useState('');

  // Assignments Dialog
  const [assignmentsDialogOpen, setAssignmentsDialogOpen] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [employeeAssignments, setEmployeeAssignments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [addingAssignment, setAddingAssignment] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [dialogError, setDialogError] = useState('');
  const [newAssignment, setNewAssignment] = useState({
    project_code: '',
    location_id: '',
    onboarding_date: '',
    offboarding_date: '',
    bill_rate: '',
    min_billable_days: '0',
    max_billable_days: '31',
    discount_percentage: '0',
  });
  const [editAssignmentData, setEditAssignmentData] = useState({
    onboarding_date: '',
    offboarding_date: '',
    bill_rate: '',
    location_id: '',
    min_billable_days: '0',
    max_billable_days: '31',
    discount_percentage: '0',
  });

  // Delete Dialogs
  const [deleteEmployeeDialogOpen, setDeleteEmployeeDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [deleteAssignmentDialogOpen, setDeleteAssignmentDialogOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);

  useEffect(() => {
    loadProjects();
    loadLocations();
    loadAllEmployees(); // Load all employees by default
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [allEmployees, selectedProject, statusFilter, nameFilter, empIdFilter]);

  const loadProjects = async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data);
    } catch (err) {
      setError('Failed to load projects');
    }
  };

  const loadLocations = async () => {
    try {
      const data = await locationService.getAll();
      setLocations(data);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const loadAllEmployees = async () => {
    try {
      setLoading(true);
      const data = await employeeService.getAll();
      setAllEmployees(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeesByProject = async (projectCode) => {
    try {
      setLoading(true);
      const data = await employeeService.getByProject(projectCode);
      setAllEmployees(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const loadUnassignedEmployees = async () => {
    try {
      setLoading(true);
      const data = await employeeService.getAll();
      // Filter to show only employees with no ACTIVE assignments
      const unassigned = data.filter(emp => {
        if (!emp.assignments || emp.assignments.length === 0) return true;

        const now = new Date();
        const hasActiveAssignment = emp.assignments.some(assignment => {
          const onboardingDate = new Date(assignment.onboarding_date);
          const offboardingDate = assignment.offboarding_date
            ? new Date(assignment.offboarding_date)
            : null;
          return onboardingDate <= now && (!offboardingDate || offboardingDate >= now);
        });

        return !hasActiveAssignment;
      });
      setAllEmployees(unassigned);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (e) => {
    const projectCode = e.target.value;
    setSelectedProject(projectCode);

    if (projectCode === 'UNASSIGNED') {
      // Load unassigned employees
      loadUnassignedEmployees();
    } else if (projectCode) {
      // Load employees for selected project
      loadEmployeesByProject(projectCode);
    } else {
      // Load all employees when no project selected (All Projects)
      loadAllEmployees();
    }
  };

  // Get project-specific status when project is selected
  const getEmployeeStatus = (employee) => {
    // When viewing "All Projects" with expanded rows, use the assignment status
    if (employee._assignmentStatus) {
      return employee._assignmentStatus;
    }

    if (!selectedProject) {
      // No project selected, use global status
      return employee.status;
    }

    // Project selected, compute status for that specific project
    if (!employee.assignments || employee.assignments.length === 0) return 'Inactive';

    const projectAssignment = employee.assignments.find(
      a => a.project_code === selectedProject
    );

    if (!projectAssignment) return 'Inactive';

    const now = new Date();
    const onboardingDate = new Date(projectAssignment.onboarding_date);
    const offboardingDate = projectAssignment.offboarding_date
      ? new Date(projectAssignment.offboarding_date)
      : null;

    // Check if currently active in this project
    const isActive = onboardingDate <= now && (!offboardingDate || offboardingDate >= now);
    return isActive ? 'Active' : 'Inactive';
  };

  const applyFiltersAndSort = () => {
    let filtered = [...allEmployees];

    // When viewing "All Projects", expand each employee into multiple rows (one per assignment)
    if (!selectedProject || selectedProject === '') {
      const expandedRows = [];
      filtered.forEach(emp => {
        if (emp.assignments && emp.assignments.length > 0) {
          // Create a row for each assignment
          emp.assignments.forEach(assignment => {
            const now = new Date();
            const onboardingDate = new Date(assignment.onboarding_date);
            const offboardingDate = assignment.offboarding_date ? new Date(assignment.offboarding_date) : null;
            const isActive = onboardingDate <= now && (!offboardingDate || offboardingDate >= now);

            expandedRows.push({
              ...emp,
              _assignmentId: assignment.id,
              _singleAssignment: assignment,
              _assignmentStatus: isActive ? 'Active' : 'Inactive'
            });
          });
        } else {
          // No assignments, show employee as-is
          expandedRows.push(emp);
        }
      });
      filtered = expandedRows;
    }

    // Apply status filter
    if (statusFilter === 'Active') {
      if (!selectedProject || selectedProject === '') {
        // Filter by assignment status when viewing all projects
        filtered = filtered.filter(emp => emp._assignmentStatus === 'Active');
      } else {
        // Filter by employee status when specific project is selected
        filtered = filtered.filter(emp => getEmployeeStatus(emp) === 'Active');
      }
    } else if (statusFilter === 'Inactive') {
      if (!selectedProject || selectedProject === '') {
        // Filter by assignment status when viewing all projects
        filtered = filtered.filter(emp => emp._assignmentStatus === 'Inactive');
      } else {
        // Filter by employee status when specific project is selected
        filtered = filtered.filter(emp => getEmployeeStatus(emp) === 'Inactive');
      }
    }

    // Apply name filter
    if (nameFilter.trim()) {
      const searchTerm = nameFilter.toLowerCase().trim();
      filtered = filtered.filter(emp =>
        emp.name && emp.name.toLowerCase().includes(searchTerm)
      );
    }

    // Apply employee ID filter
    if (empIdFilter.trim()) {
      const searchTerm = empIdFilter.toLowerCase().trim();
      filtered = filtered.filter(emp =>
        emp.emp_id && emp.emp_id.toLowerCase().includes(searchTerm)
      );
    }

    // Custom sorting
    filtered.sort((a, b) => {
      let aActive, bActive;

      if (!selectedProject || selectedProject === '') {
        // Sort by assignment status when viewing all projects
        aActive = a._assignmentStatus === 'Active';
        bActive = b._assignmentStatus === 'Active';
      } else {
        // Sort by employee status when specific project is selected
        aActive = getEmployeeStatus(a) === 'Active';
        bActive = getEmployeeStatus(b) === 'Active';
      }

      // Active employees come first
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;

      // Both active or both inactive
      if (aActive && bActive) {
        // Active: sort by start date ascending (earliest first)
        const aStartDate = getProjectStartDate(a);
        const bStartDate = getProjectStartDate(b);
        return new Date(aStartDate) - new Date(bStartDate);
      } else {
        // Inactive: sort by offboarding date descending (most recent first)
        const aEndDate = getProjectEndDate(a);
        const bEndDate = getProjectEndDate(b);
        return new Date(bEndDate) - new Date(aEndDate);
      }
    });

    setFilteredEmployees(filtered);
  };

  const getProjectStartDate = (employee) => {
    // When viewing "All Projects" with expanded rows, use the single assignment
    if (employee._singleAssignment) {
      return employee._singleAssignment.onboarding_date;
    }

    if (!employee.assignments || employee.assignments.length === 0) return null;

    // If project is selected, get start date for that specific project
    if (selectedProject) {
      const projectAssignment = employee.assignments.find(
        a => a.project_code === selectedProject
      );
      return projectAssignment?.onboarding_date || null;
    }

    // Fallback: get start date from active assignment only
    const activeAssignment = getProjectAssignment(employee);
    return activeAssignment?.onboarding_date || null;
  };

  const getProjectEndDate = (employee) => {
    // When viewing "All Projects" with expanded rows, use the single assignment
    if (employee._singleAssignment) {
      return employee._singleAssignment.offboarding_date;
    }

    if (!employee.assignments || employee.assignments.length === 0) return null;

    // If project is selected, get end date for that specific project
    if (selectedProject) {
      const projectAssignment = employee.assignments.find(
        a => a.project_code === selectedProject
      );
      return projectAssignment?.offboarding_date || null;
    }

    // Fallback: get end date from active assignment only
    const activeAssignment = getProjectAssignment(employee);
    return activeAssignment?.offboarding_date || null;
  };

  const getProjectAssignment = (employee) => {
    // When viewing "All Projects" with expanded rows, use the single assignment
    if (employee._singleAssignment) {
      return employee._singleAssignment;
    }

    if (!employee.assignments || employee.assignments.length === 0) return null;

    // If project is selected, return that specific assignment
    if (selectedProject) {
      return employee.assignments.find(a => a.project_code === selectedProject);
    }

    // Otherwise, return the active assignment
    const now = new Date();
    return employee.assignments.find(assignment => {
      const onboardingDate = new Date(assignment.onboarding_date);
      const offboardingDate = assignment.offboarding_date ? new Date(assignment.offboarding_date) : null;
      return onboardingDate <= now && (!offboardingDate || offboardingDate >= now);
    }) || employee.assignments[0]; // Fallback to first assignment
  };

  // Get all project codes for display when viewing "All Projects"
  const getProjectDisplay = (employee) => {
    if (!employee.assignments || employee.assignments.length === 0) return '-';

    // If specific project is selected, show only that project
    if (selectedProject) {
      const assignment = employee.assignments.find(a => a.project_code === selectedProject);
      return assignment?.project?.project_code || selectedProject;
    }

    // When viewing "All Projects", show all projects with status indicators
    const now = new Date();
    const projectsWithStatus = employee.assignments.map(assignment => {
      const onboardingDate = new Date(assignment.onboarding_date);
      const offboardingDate = assignment.offboarding_date ? new Date(assignment.offboarding_date) : null;
      const isActive = onboardingDate <= now && (!offboardingDate || offboardingDate >= now);
      return {
        code: assignment.project?.project_code || assignment.project_code,
        isActive
      };
    });

    return projectsWithStatus;
  };

  // Render project display as chips
  const renderProjectDisplay = (employee) => {
    // When viewing "All Projects" with expanded rows, show single project
    if (employee._singleAssignment) {
      const assignment = employee._singleAssignment;
      return assignment.project?.project_code || assignment.project_code || '-';
    }

    const projects = getProjectDisplay(employee);
    if (projects === '-') return '-';

    // Array of projects with status
    if (Array.isArray(projects)) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {projects.map((proj, index) => (
            <Chip
              key={index}
              label={proj.code}
              size="small"
              color={proj.isActive ? 'primary' : 'default'}
              variant={proj.isActive ? 'filled' : 'outlined'}
              sx={{ fontSize: '0.75rem' }}
            />
          ))}
        </Box>
      );
    }

    // Single project code string
    return projects;
  };

  // Get bill rate to display (project-specific or active)
  // Uses effective_bill_rate which considers bill rate overrides
  const getBillRate = (employee) => {
    // When viewing "All Projects" with expanded rows, use the single assignment
    if (employee._singleAssignment) {
      return employee._singleAssignment.effective_bill_rate ?? employee._singleAssignment.bill_rate;
    }

    if (!employee.assignments || employee.assignments.length === 0) return null;

    // If project is selected, get bill rate from that specific project's assignment
    if (selectedProject) {
      const projectAssignment = employee.assignments.find(
        a => a.project_code === selectedProject
      );
      return projectAssignment?.effective_bill_rate ?? projectAssignment?.bill_rate ?? null;
    }

    // Otherwise, use active_bill_rate (considers overrides from backend)
    return employee.active_bill_rate || null;
  };

  const handleAddEmployee = () => {
    navigate('/employees/new');
  };

  const handleEditEmployee = (empId) => {
    // Pass selected project code as query parameter to filter assignments
    if (selectedProject && selectedProject !== 'UNASSIGNED') {
      navigate(`/employees/edit/${empId}?project=${selectedProject}`);
    } else {
      navigate(`/employees/edit/${empId}`);
    }
  };

  const handleDeleteEmployeeClick = (employee) => {
    setEmployeeToDelete(employee);
    setDeleteEmployeeDialogOpen(true);
  };

  const handleDeleteEmployeeConfirm = async () => {
    if (!employeeToDelete) return;
    try {
      await employeeService.delete(employeeToDelete.id);
      setSuccess('Employee deleted successfully');

      // Reload based on current filter
      if (selectedProject === 'UNASSIGNED') {
        loadUnassignedEmployees();
      } else if (selectedProject) {
        loadEmployeesByProject(selectedProject);
      } else {
        loadAllEmployees();
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete employee');
      setTimeout(() => setError(''), 3000);
    } finally {
      setDeleteEmployeeDialogOpen(false);
      setEmployeeToDelete(null);
    }
  };

  const handleDeleteEmployeeCancel = () => {
    setDeleteEmployeeDialogOpen(false);
    setEmployeeToDelete(null);
  };

  // Helper function to filter assignments by selected project
  const filterAssignmentsByProject = (assignments) => {
    if (!selectedProject || selectedProject === 'UNASSIGNED') {
      return assignments || [];
    }
    return (assignments || []).filter(
      assignment => assignment.project_code === selectedProject
    );
  };

  const handleViewAssignments = async (employee) => {
    setCurrentEmployee(employee);
    setEmployeeAssignments(filterAssignmentsByProject(employee.assignments));
    setAssignmentsDialogOpen(true);
    setAddingAssignment(false);
    setDialogError('');
  };

  const handleCloseAssignmentsDialog = () => {
    setAssignmentsDialogOpen(false);
    setCurrentEmployee(null);
    setEmployeeAssignments([]);
    setAddingAssignment(false);
    setNewAssignment({
      project_code: '',
      location_id: '',
      onboarding_date: '',
      offboarding_date: '',
      bill_rate: '',
      min_billable_days: '0',
      max_billable_days: '31',
      discount_percentage: '0',
    });
  };

  const handleAddAssignmentClick = () => {
    setAddingAssignment(true);
  };

  const handleCancelAddAssignment = () => {
    setAddingAssignment(false);
    setNewAssignment({
      project_code: '',
      location_id: '',
      onboarding_date: '',
      offboarding_date: '',
      bill_rate: '',
      min_billable_days: '0',
      max_billable_days: '31',
      discount_percentage: '0',
    });
  };

  const handleNewAssignmentChange = (e) => {
    const { name, value } = e.target;
    setNewAssignment((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveAssignment = async () => {
    try {
      setDialogError('');
      if (!newAssignment.project_code || !newAssignment.location_id || !newAssignment.onboarding_date || !newAssignment.bill_rate) {
        setDialogError('Please fill in all required fields for the assignment');
        return;
      }

      await assignmentService.create({
        emp_id: currentEmployee.id,
        project_code: newAssignment.project_code,
        location_id: newAssignment.location_id,
        onboarding_date: newAssignment.onboarding_date,
        offboarding_date: newAssignment.offboarding_date || null,
        bill_rate: parseFloat(newAssignment.bill_rate),
        min_billable_days: parseInt(newAssignment.min_billable_days) || 0,
        max_billable_days: parseInt(newAssignment.max_billable_days) || 31,
        discount_percentage: parseFloat(newAssignment.discount_percentage) || 0,
      });

      setSuccess('Assignment added successfully');
      setTimeout(() => setSuccess(''), 3000);

      // Reload employee data
      if (selectedProject === 'UNASSIGNED') {
        await loadUnassignedEmployees();
      } else if (selectedProject) {
        await loadEmployeesByProject(selectedProject);
      } else {
        await loadAllEmployees();
      }

      // Refresh the dialog
      const updatedEmployee = await employeeService.getById(currentEmployee.id);
      setCurrentEmployee(updatedEmployee);
      setEmployeeAssignments(filterAssignmentsByProject(updatedEmployee.assignments));
      setAddingAssignment(false);
      setNewAssignment({
        project_code: '',
        location_id: '',
        onboarding_date: '',
        offboarding_date: '',
        bill_rate: '',
        min_billable_days: '0',
        max_billable_days: '31',
        discount_percentage: '0',
      });
    } catch (err) {
      setDialogError(err.response?.data?.error || 'Failed to add assignment');
    }
  };

  const handleDeleteAssignmentClick = (assignment) => {
    setAssignmentToDelete(assignment);
    setDeleteAssignmentDialogOpen(true);
  };

  const handleDeleteAssignmentConfirm = async () => {
    if (!assignmentToDelete) return;
    try {
      await assignmentService.delete(assignmentToDelete.id);
      setSuccess('Assignment deleted successfully');
      setTimeout(() => setSuccess(''), 3000);

      // Reload employee data
      if (selectedProject === 'UNASSIGNED') {
        await loadUnassignedEmployees();
      } else if (selectedProject) {
        await loadEmployeesByProject(selectedProject);
      } else {
        await loadAllEmployees();
      }

      // Refresh the dialog
      const updatedEmployee = await employeeService.getById(currentEmployee.id);
      setCurrentEmployee(updatedEmployee);
      setEmployeeAssignments(filterAssignmentsByProject(updatedEmployee.assignments));
    } catch (err) {
      setDialogError(err.response?.data?.error || 'Failed to delete assignment');
    } finally {
      setDeleteAssignmentDialogOpen(false);
      setAssignmentToDelete(null);
    }
  };

  const handleDeleteAssignmentCancel = () => {
    setDeleteAssignmentDialogOpen(false);
    setAssignmentToDelete(null);
  };

  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment.id);
    setEditAssignmentData({
      onboarding_date: assignment.onboarding_date.split('T')[0],
      offboarding_date: assignment.offboarding_date ? assignment.offboarding_date.split('T')[0] : '',
      bill_rate: assignment.bill_rate.toString(),
      location_id: assignment.location_id || '',
      min_billable_days: (assignment.min_billable_days || 0).toString(),
      max_billable_days: (assignment.max_billable_days || 31).toString(),
      discount_percentage: (assignment.discount_percentage || 0).toString(),
    });
    setAddingAssignment(false);
  };

  const handleCancelEditAssignment = () => {
    setEditingAssignment(null);
    setEditAssignmentData({
      onboarding_date: '',
      offboarding_date: '',
      bill_rate: '',
      location_id: '',
      min_billable_days: '0',
      max_billable_days: '31',
      discount_percentage: '0',
    });
  };

  const handleEditAssignmentChange = (e) => {
    const { name, value } = e.target;
    setEditAssignmentData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEditAssignment = async () => {
    try {
      setDialogError('');
      if (!editAssignmentData.onboarding_date || !editAssignmentData.bill_rate || !editAssignmentData.location_id) {
        setDialogError('Please fill in all required fields');
        return;
      }

      await assignmentService.update(editingAssignment, {
        onboarding_date: editAssignmentData.onboarding_date,
        offboarding_date: editAssignmentData.offboarding_date || null,
        bill_rate: parseFloat(editAssignmentData.bill_rate),
        location_id: editAssignmentData.location_id,
        min_billable_days: parseInt(editAssignmentData.min_billable_days) || 0,
        max_billable_days: parseInt(editAssignmentData.max_billable_days) || 31,
        discount_percentage: parseFloat(editAssignmentData.discount_percentage) || 0,
      });

      setSuccess('Assignment updated successfully');
      setTimeout(() => setSuccess(''), 3000);

      // Reload employee data
      if (selectedProject === 'UNASSIGNED') {
        await loadUnassignedEmployees();
      } else if (selectedProject) {
        await loadEmployeesByProject(selectedProject);
      } else {
        await loadAllEmployees();
      }

      // Refresh the dialog
      const updatedEmployee = await employeeService.getById(currentEmployee.id);
      setCurrentEmployee(updatedEmployee);
      setEmployeeAssignments(filterAssignmentsByProject(updatedEmployee.assignments));
      setEditingAssignment(null);
      setEditAssignmentData({
        onboarding_date: '',
        offboarding_date: '',
        bill_rate: '',
        location_id: '',
        min_billable_days: '0',
        max_billable_days: '31',
        discount_percentage: '0',
      });
    } catch (err) {
      setDialogError(err.response?.data?.error || 'Failed to update assignment');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Export to Excel
  const exportToExcel = () => {
    if (!filteredEmployees || filteredEmployees.length === 0) return;

    const headers = ['SNo', 'Employee ID', 'Name', 'Status', 'Project', 'Location', 'Start Date', 'End Date', 'Bill Rate'];

    const dataRows = filteredEmployees.map((employee, index) => {
      const assignment = getProjectAssignment(employee);
      const startDate = getProjectStartDate(employee);
      const endDate = getProjectEndDate(employee);
      const employeeStatus = getEmployeeStatus(employee);
      const billRate = getBillRate(employee);
      const projectCode = employee._singleAssignment
        ? (employee._singleAssignment.project?.project_code || employee._singleAssignment.project_code || '-')
        : (assignment?.project?.project_code || assignment?.project_code || '-');

      return [
        index + 1,
        employee.emp_id,
        employee.name,
        employeeStatus,
        projectCode,
        assignment?.location?.location_name || '-',
        startDate ? format(new Date(startDate), 'MMM dd, yyyy') : '-',
        endDate ? format(new Date(endDate), 'MMM dd, yyyy') : '-',
        billRate ? `$${billRate}/hr` : '-',
      ];
    });

    const wsData = [headers, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    headers.forEach((_, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      let bgColor = '5C6BC0';
      if (index === 8) bgColor = '2E7D32';

      if (ws[cellRef]) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: bgColor } },
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: { bottom: { style: 'medium', color: { rgb: '000000' } } },
        };
      }
    });

    dataRows.forEach((row, rowIndex) => {
      row.forEach((_, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
        if (ws[cellRef]) {
          ws[cellRef].s = {
            fill: { fgColor: { rgb: rowIndex % 2 === 0 ? 'FFFFFF' : 'E8EAF6' } },
            alignment: { horizontal: 'left', vertical: 'center' },
          };
        }
      });
    });

    ws['!cols'] = [
      { wch: 6 }, { wch: 12 }, { wch: 20 }, { wch: 10 }, { wch: 12 },
      { wch: 15 }, { wch: 14 }, { wch: 14 }, { wch: 12 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employees');
    XLSX.writeFile(wb, `Employees_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!filteredEmployees || filteredEmployees.length === 0) return;

    const doc = new jsPDF('landscape');

    doc.setFontSize(18);
    doc.setTextColor(92, 107, 192);
    doc.text('Employee List', 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 28);

    const headers = [['SNo', 'Employee ID', 'Name', 'Status', 'Project', 'Location', 'Start Date', 'End Date', 'Bill Rate']];

    const data = filteredEmployees.map((employee, index) => {
      const assignment = getProjectAssignment(employee);
      const startDate = getProjectStartDate(employee);
      const endDate = getProjectEndDate(employee);
      const employeeStatus = getEmployeeStatus(employee);
      const billRate = getBillRate(employee);
      const projectCode = employee._singleAssignment
        ? (employee._singleAssignment.project?.project_code || employee._singleAssignment.project_code || '-')
        : (assignment?.project?.project_code || assignment?.project_code || '-');

      return [
        index + 1,
        employee.emp_id,
        employee.name,
        employeeStatus,
        projectCode,
        assignment?.location?.location_name || '-',
        startDate ? format(new Date(startDate), 'MMM dd, yyyy') : '-',
        endDate ? format(new Date(endDate), 'MMM dd, yyyy') : '-',
        billRate ? `$${billRate}/hr` : '-',
      ];
    });

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 35,
      theme: 'grid',
      headStyles: {
        fillColor: [92, 107, 192],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        8: { halign: 'right', fillColor: [232, 245, 233] },
      },
      alternateRowStyles: { fillColor: [232, 234, 246] },
    });

    doc.save(`Employees_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Calculate stats based on what's displayed
  const totalEmployees = filteredEmployees.length;
  const activeEmployees = filteredEmployees.filter(emp => {
    // Use the same status logic as the display
    if (emp._assignmentStatus) {
      return emp._assignmentStatus === 'Active';
    }
    if (!selectedProject) {
      return emp.status === 'Active';
    }
    // For project-specific view, check assignment status
    const assignment = emp.assignments?.find(a => a.project_code === selectedProject);
    if (!assignment) return false;
    const now = new Date();
    const onboardingDate = new Date(assignment.onboarding_date);
    const offboardingDate = assignment.offboarding_date ? new Date(assignment.offboarding_date) : null;
    return onboardingDate <= now && (!offboardingDate || offboardingDate >= now);
  }).length;
  const inactiveEmployees = totalEmployees - activeEmployees;

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <PageHeader
          title="Employees"
          subtitle={`${totalEmployees} total employees, ${activeEmployees} active`}
          icon={PeopleIcon}
          iconColor="#5C6BC0"
          action={handleAddEmployee}
          actionLabel="Add Employee"
          actionIcon={AddIcon}
          extraActions={
            <Box sx={{ display: 'flex', gap: 1, mr: 1 }}>
              <Tooltip title="Export to PDF">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={exportToPDF}
                  disabled={filteredEmployees.length === 0}
                  sx={{
                    borderColor: '#d32f2f',
                    color: '#d32f2f',
                    '&:hover': { backgroundColor: '#ffebee', borderColor: '#c62828' },
                  }}
                >
                  PDF
                </Button>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TableChartIcon />}
                  onClick={exportToExcel}
                  disabled={filteredEmployees.length === 0}
                  sx={{
                    borderColor: '#2e7d32',
                    color: '#2e7d32',
                    '&:hover': { backgroundColor: '#e8f5e9', borderColor: '#1b5e20' },
                  }}
                >
                  Excel
                </Button>
              </Tooltip>
            </Box>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Employee ID"
                value={empIdFilter}
                onChange={(e) => setEmpIdFilter(e.target.value)}
                placeholder="Search by ID..."
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Employee Name"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Search by name..."
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Project Code"
                value={selectedProject}
                onChange={handleProjectChange}
                size="small"
              >
                <MenuItem value="">-- All Projects --</MenuItem>
                <MenuItem value="UNASSIGNED">Unassigned Employees</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.project_code} value={project.project_code}>
                    {project.project_code} - {project.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {/* Employee Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellStyle, width: 60 }} align="center">SNo</TableCell>
                <TableCell sx={headerCellStyle}>Employee ID</TableCell>
                <TableCell sx={headerCellStyle}>Name</TableCell>
                <TableCell sx={headerCellStyle}>Status</TableCell>
                <TableCell sx={headerCellStyle}>Project</TableCell>
                <TableCell sx={headerCellStyle}>Location</TableCell>
                <TableCell sx={headerCellStyle}>Start Date</TableCell>
                <TableCell sx={headerCellStyle}>End Date</TableCell>
                <TableCell align="center" sx={{ ...headerCellStyle, backgroundColor: '#2E7D32', borderBottom: '3px solid #1B5E20' }}>Bill Rate</TableCell>
                <TableCell align="center" sx={headerCellStyle}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    {selectedProject === 'UNASSIGNED'
                      ? 'No unassigned employees found'
                      : selectedProject
                      ? 'No employees found for the selected project and status filter'
                      : 'No employees found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((employee, index) => {
                  const assignment = getProjectAssignment(employee);
                  const startDate = getProjectStartDate(employee);
                  const endDate = getProjectEndDate(employee);
                  const employeeStatus = getEmployeeStatus(employee);
                  const billRate = getBillRate(employee);

                  // Generate unique key: use assignment ID if available (for expanded rows), otherwise use employee ID
                  const rowKey = employee._assignmentId ? `${employee.id}-${employee._assignmentId}` : employee.id;

                  return (
                    <TableRow
                      key={rowKey}
                      sx={{
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#E8EAF6',
                        '&:hover': { backgroundColor: '#C5CAE9' },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell><strong>{employee.emp_id}</strong></TableCell>
                      <TableCell>{employee.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={employeeStatus}
                          color={employeeStatus === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {renderProjectDisplay(employee)}
                      </TableCell>
                      <TableCell>
                        {assignment?.location?.location_name || '-'}
                      </TableCell>
                      <TableCell>
                        {startDate ? format(new Date(startDate), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        {endDate ? format(new Date(endDate), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {billRate ? `$${billRate}/hr` : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="info"
                          onClick={() => handleViewAssignments(employee)}
                          size="small"
                          sx={{ mr: 1 }}
                          title="View and manage assignments"
                        >
                          <AssignmentIcon />
                        </IconButton>
                        <IconButton
                          color="primary"
                          onClick={() => handleEditEmployee(employee.id)}
                          size="small"
                          sx={{ mr: 1 }}
                          title="Edit employee details"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteEmployeeClick(employee)}
                          size="small"
                          title="Delete employee"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Assignments Dialog */}
      <Dialog open={assignmentsDialogOpen} onClose={handleCloseAssignmentsDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Assignments for {currentEmployee?.name} ({currentEmployee?.emp_id})
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          {employeeAssignments.length === 0 && !addingAssignment ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No assignments found. Click "Add Assignment" to create one.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Project</strong></TableCell>
                    <TableCell><strong>Location</strong></TableCell>
                    <TableCell><strong>Onboarding</strong></TableCell>
                    <TableCell><strong>Offboarding</strong></TableCell>
                    <TableCell align="right"><strong>Bill Rate</strong></TableCell>
                    <TableCell align="right"><strong>Min Days</strong></TableCell>
                    <TableCell align="right"><strong>Max Days</strong></TableCell>
                    <TableCell align="right"><strong>Discount %</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employeeAssignments.map((assignment) => {
                    const now = new Date();
                    const onboardingDate = new Date(assignment.onboarding_date);
                    const offboardingDate = assignment.offboarding_date
                      ? new Date(assignment.offboarding_date)
                      : null;
                    const isActive = onboardingDate <= now && (!offboardingDate || offboardingDate >= now);

                    return (
                      <TableRow key={assignment.id}>
                        <TableCell>
                          {assignment.project?.project_code || assignment.project_code}
                        </TableCell>
                        <TableCell>
                          {assignment.location?.location_name || '-'}
                        </TableCell>
                        <TableCell>
                          {format(new Date(assignment.onboarding_date), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell>
                          {assignment.offboarding_date
                            ? format(new Date(assignment.offboarding_date), 'MMM dd, yyyy')
                            : 'Ongoing'}
                        </TableCell>
                        <TableCell align="right">
                          ${assignment.effective_bill_rate ?? assignment.bill_rate}/hr
                          {assignment.bill_rate_source === 'override' && (
                            <Chip label="Override" size="small" color="warning" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                          )}
                        </TableCell>
                        <TableCell align="right">{assignment.min_billable_days || 0}</TableCell>
                        <TableCell align="right">{assignment.max_billable_days || 31}</TableCell>
                        <TableCell align="right">{assignment.discount_percentage || 0}%</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={isActive ? 'Active' : 'Inactive'}
                            color={isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            onClick={() => handleEditAssignment(assignment)}
                            size="small"
                            title="Edit assignment"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteAssignmentClick(assignment)}
                            size="small"
                            title="Delete assignment"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Add Assignment Form */}
          {addingAssignment && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Add New Assignment
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Project *"
                    name="project_code"
                    value={newAssignment.project_code}
                    onChange={handleNewAssignmentChange}
                    required
                  >
                    {projects.map((project) => (
                      <MenuItem key={project.project_code} value={project.project_code}>
                        {project.project_code} - {project.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Location *"
                    name="location_id"
                    value={newAssignment.location_id}
                    onChange={handleNewAssignmentChange}
                    required
                  >
                    {locations.map((location) => (
                      <MenuItem key={location.location_id} value={location.location_id}>
                        {location.location_name} ({location.country})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Onboarding Date *"
                    name="onboarding_date"
                    type="date"
                    value={newAssignment.onboarding_date}
                    onChange={handleNewAssignmentChange}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Offboarding Date"
                    name="offboarding_date"
                    type="date"
                    value={newAssignment.offboarding_date}
                    onChange={handleNewAssignmentChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bill Rate (per hour) *"
                    name="bill_rate"
                    type="number"
                    value={newAssignment.bill_rate}
                    onChange={handleNewAssignmentChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Min Billable Days"
                    name="min_billable_days"
                    type="number"
                    value={newAssignment.min_billable_days}
                    onChange={handleNewAssignmentChange}
                    helperText="Minimum billable days per month"
                    inputProps={{ min: 0, max: 31 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Max Billable Days"
                    name="max_billable_days"
                    type="number"
                    value={newAssignment.max_billable_days}
                    onChange={handleNewAssignmentChange}
                    helperText="Maximum billable days per month"
                    inputProps={{ min: 0, max: 31 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Discount %"
                    name="discount_percentage"
                    type="number"
                    value={newAssignment.discount_percentage}
                    onChange={handleNewAssignmentChange}
                    helperText="Discount percentage (0-100)"
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button variant="contained" color="primary" onClick={handleSaveAssignment}>
                  Save Assignment
                </Button>
                <Button variant="outlined" onClick={handleCancelAddAssignment}>
                  Cancel
                </Button>
              </Box>
            </Paper>
          )}

          {/* Edit Assignment Form */}
          {editingAssignment && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Edit Assignment
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Location *"
                    name="location_id"
                    value={editAssignmentData.location_id}
                    onChange={handleEditAssignmentChange}
                    required
                  >
                    {locations.map((location) => (
                      <MenuItem key={location.location_id} value={location.location_id}>
                        {location.location_name} ({location.country})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bill Rate (per hour) *"
                    name="bill_rate"
                    type="number"
                    value={editAssignmentData.bill_rate}
                    onChange={handleEditAssignmentChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Onboarding Date *"
                    name="onboarding_date"
                    type="date"
                    value={editAssignmentData.onboarding_date}
                    onChange={handleEditAssignmentChange}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Offboarding Date"
                    name="offboarding_date"
                    type="date"
                    value={editAssignmentData.offboarding_date}
                    onChange={handleEditAssignmentChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Min Billable Days"
                    name="min_billable_days"
                    type="number"
                    value={editAssignmentData.min_billable_days}
                    onChange={handleEditAssignmentChange}
                    helperText="Minimum billable days per month"
                    inputProps={{ min: 0, max: 31 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Max Billable Days"
                    name="max_billable_days"
                    type="number"
                    value={editAssignmentData.max_billable_days}
                    onChange={handleEditAssignmentChange}
                    helperText="Maximum billable days per month"
                    inputProps={{ min: 0, max: 31 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Discount %"
                    name="discount_percentage"
                    type="number"
                    value={editAssignmentData.discount_percentage}
                    onChange={handleEditAssignmentChange}
                    helperText="Discount percentage (0-100)"
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button variant="contained" color="primary" onClick={handleSaveEditAssignment}>
                  Update Assignment
                </Button>
                <Button variant="outlined" onClick={handleCancelEditAssignment}>
                  Cancel
                </Button>
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          {!addingAssignment && !editingAssignment && (
            <Button startIcon={<AddIcon />} onClick={handleAddAssignmentClick} color="primary">
              Add Assignment
            </Button>
          )}
          <Button onClick={handleCloseAssignmentsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Employee Confirmation Dialog */}
      <ConfirmDialog
        open={deleteEmployeeDialogOpen}
        onClose={handleDeleteEmployeeCancel}
        onConfirm={handleDeleteEmployeeConfirm}
        title="Delete Employee"
        message="Are you sure you want to delete this employee? This will also delete all their assignments and billing records."
        details={employeeToDelete ? [
          { label: 'Emp ID', value: employeeToDelete.emp_id },
          { label: 'Name', value: employeeToDelete.name },
        ] : []}
      />

      {/* Delete Assignment Confirmation Dialog */}
      <ConfirmDialog
        open={deleteAssignmentDialogOpen}
        onClose={handleDeleteAssignmentCancel}
        onConfirm={handleDeleteAssignmentConfirm}
        title="Delete Assignment"
        message="Are you sure you want to delete this assignment?"
        details={assignmentToDelete ? [
          { label: 'Project', value: assignmentToDelete.project?.project_code || assignmentToDelete.project_code },
        ] : []}
      />
    </Container>
  );
};

export default EmployeeList;
